/**
 * Utility functions for converting perceptual adjustment values to shader/filter values
 * Feature: 011-light-editing
 * Task: T018
 */

/**
 * Converts exposure adjustment (EV stops) to brightness multiplier
 * Exposure in photography is measured in stops, where each stop represents a doubling or halving of light.
 * Formula: multiplier = 2^ev
 *
 * @param ev - Exposure value in EV stops (-2.0 to +2.0)
 * @returns Brightness multiplier (0.25 to 4.0)
 *
 * @example
 * exposureToMultiplier(0)    // 1.0 (no change)
 * exposureToMultiplier(1)    // 2.0 (double brightness)
 * exposureToMultiplier(-1)   // 0.5 (half brightness)
 * exposureToMultiplier(2)    // 4.0 (quadruple brightness)
 * exposureToMultiplier(-2)   // 0.25 (quarter brightness)
 */
export function exposureToMultiplier(ev: number): number {
  return Math.pow(2, ev)
}

/**
 * Converts contrast adjustment to filter multiplier
 * Contrast of 0 is neutral, positive increases contrast, negative decreases contrast.
 * We map -1..1 to a multiplier range that works well visually.
 *
 * @param contrast - Contrast adjustment (-1.0 to +1.0)
 * @returns Contrast multiplier (0.0 to 2.0, where 1.0 is neutral)
 *
 * @example
 * contrastToMultiplier(0)    // 1.0 (no change)
 * contrastToMultiplier(1)    // 2.0 (maximum contrast)
 * contrastToMultiplier(-1)   // 0.0 (minimum contrast, flat gray)
 * contrastToMultiplier(0.5)  // 1.5
 */
export function contrastToMultiplier(contrast: number): number {
  // Map -1..1 to 0..2, where 1.0 is neutral
  return contrast + 1.0
}

/**
 * Normalizes an adjustment value from its perceptual range to a 0..1 shader range
 * Used for highlights, shadows, whites, blacks adjustments
 *
 * @param value - Adjustment value
 * @param range - Min/max range for this adjustment
 * @returns Normalized value (0.0 to 1.0, where 0.5 is neutral)
 *
 * @example
 * adjustmentToShaderValue(0, {min: -100, max: 100})   // 0.5 (neutral)
 * adjustmentToShaderValue(100, {min: -100, max: 100}) // 1.0 (maximum)
 * adjustmentToShaderValue(-100, {min: -100, max: 100}) // 0.0 (minimum)
 * adjustmentToShaderValue(50, {min: -100, max: 100})  // 0.75
 */
export function adjustmentToShaderValue(
  value: number,
  range: { min: number; max: number }
): number {
  // Normalize to 0..1 range
  return (value - range.min) / (range.max - range.min)
}

/**
 * Converts a normalized shader value back to perceptual range
 * Inverse of adjustmentToShaderValue
 *
 * @param normalized - Shader value (0.0 to 1.0)
 * @param range - Min/max range for this adjustment
 * @returns Perceptual adjustment value
 *
 * @example
 * shaderValueToAdjustment(0.5, {min: -100, max: 100})  // 0
 * shaderValueToAdjustment(1.0, {min: -100, max: 100})  // 100
 * shaderValueToAdjustment(0.0, {min: -100, max: 100})  // -100
 */
export function shaderValueToAdjustment(
  normalized: number,
  range: { min: number; max: number }
): number {
  return normalized * (range.max - range.min) + range.min
}

/**
 * Clamps a value to the specified range
 * Ensures adjustment values don't exceed their valid bounds
 *
 * @param value - Value to clamp
 * @param min - Minimum allowed value
 * @param max - Maximum allowed value
 * @returns Clamped value
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

/**
 * Rounds a value to the nearest step increment
 * Useful for snapping slider values to increments (e.g., 0.1 for exposure)
 *
 * @param value - Value to round
 * @param step - Step increment
 * @returns Rounded value
 *
 * @example
 * roundToStep(0.47, 0.1)  // 0.5
 * roundToStep(23, 5)      // 25
 * roundToStep(1.234, 0.01) // 1.23
 */
export function roundToStep(value: number, step: number): number {
  return Math.round(value / step) * step
}
